import { searchStore } from "features/ProductSearch/SearchStore";
import ProductDetails from "../components/ProductDetails";
import useDetailsSections, { Section } from "../useProducDetalsModal";
import ComponentDetails from "./ComponentDetails";
import { observer } from "mobx-react-lite";
import Alert from "components/Alert";
import { useTranslation } from "react-i18next";
import {
  ComponentDetailsByFileOut,
  InventoryProductDetailsByFileOut,
} from "api-client";
import { useComplianceInfo } from "hooks/useComplianceInfo";
import ImageHeader from "../components/ImageHeader";
import React from "react";
import SectionsNavMenu from "../components/SectionsNavMenu";
import clsx from "clsx";

function DetailsView({
  hasWarnings,
  isComponent,
  details,
}: {
  hasWarnings: boolean;
  isComponent: boolean;
  details?: InventoryProductDetailsByFileOut | ComponentDetailsByFileOut;
}) {
  const { t } = useTranslation();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { sectionItems, getProductIcon } = useDetailsSections(
    searchStore.currentDetailNode
  );
  const { getCurrentCertification, getCurrentConformity } = useComplianceInfo();
  const isCompliant = searchStore.currentDetailNode?.is_compliant_by_file;
  const isOverview =
    isComponent && searchStore.currentDetailNode?.id === details?.id;

  const [activeSection, setActiveSection] = React.useState<string>(
    sectionItems[0].id
  );

  function showAlerts() {
    return (
      <>
        {isComponent && isOverview && hasWarnings ? (
          <Alert
            description={t("mapping.componentAlertOverview")}
            className="mb-6"
          />
        ) : null}

        {!isCompliant && !isOverview ? (
          <Alert
            type="attention"
            description={t(
              searchStore.openProductSearch.state
                ? "mapping.notComplianceAlert"
                : "mapping.notComplianceAlertME",
              {
                conformity: getCurrentConformity(),
                certification: getCurrentCertification()?.name,
              }
            )}
            className="mb-6"
          />
        ) : null}

        {searchStore.currentDetailNode?.product_disabled_status ===
          "DISABLED" && (
          <Alert
            type="attention"
            description={t("mapping.productDisabledAlert", {
              conformity: getCurrentConformity(),
              certification: getCurrentCertification()?.name,
            })}
            className="mb-6"
          />
        )}

        {searchStore.currentDetailNode?.product_disabled_status ===
          "UPDATE_AVAILABLE" && (
          <Alert
            type="attention"
            description={t("mapping.updatedProductAlert", {
              conformity: getCurrentConformity(),
              certification: getCurrentCertification()?.name,
            })}
            className="mb-6"
          />
        )}
      </>
    );
  }

  React.useEffect(() => {
    containerRef.current?.scrollTo({ top: 0 });
    setActiveSection(sectionItems[0].id);
  }, [searchStore.currentDetailNode?.id]);

  const imageURL = isComponent
    ? searchStore.currentDetailNode?.thumbnail_url
    : (details as InventoryProductDetailsByFileOut).files_info?.images?.[0]
        ?.thumbnail_url;

  const diagramURL = isComponent
    ? searchStore.currentDetailNode?.diagram_url
    : (details as InventoryProductDetailsByFileOut).files_info?.diagrams?.[0]
        ?.url;

  const showingDiagramOnHeader = !imageURL && !!diagramURL;

  function moveToSection(item: Section) {
    setActiveSection(item.id);
  }

  const handleScroll = () => {
    let current = activeSection;
    sectionItems.forEach((item) => {
      const el = item.ref.current;
      if (el && containerRef.current) {
        const containerTop = containerRef.current.scrollTop;
        const offset = 150;
        const sectionTop = el.offsetTop - offset;
        if (containerTop >= sectionTop) {
          current = item.id;
        }
        searchStore.setNavigationIsSticky(containerTop >= 240);
      }
    });
    setActiveSection(current);
  };

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className={clsx(
        "w-full max-w-full overflow-y-auto col-span-6 min-h-full",
        {
          "min-h-[calc(100vh-68px)] !max-h-[calc(100vh-130px)]":
            imageURL && !searchStore.navigationIsSticky,
          "!max-h-[calc(100vh-160px)]":
            imageURL && searchStore.navigationIsSticky,
          "!max-h-[calc(100vh-150px)]": !imageURL,
        }
      )}
    >
      {imageURL ? (
        <>
          <ImageHeader
            imageURL={imageURL}
            details={
              searchStore.currentDetailNode as InventoryProductDetailsByFileOut
            }
            refUnit={searchStore.currentDetailNode?.reference_unit}
            productName={searchStore.currentDetailNode?.name}
          />

          <SectionsNavMenu
            clickedMenuItem={activeSection}
            menuItems={sectionItems}
            moveToSection={moveToSection}
          />
        </>
      ) : (
        !isOverview &&
        isComponent && (
          <div
            className={clsx(
              "sticky top-0 flex items-center border-b border-gray-200 px-6 py-[14px]",
              "text-lg font-semibold h-[52px] z-3",
              searchStore.currentDetailNode?.type === "COMPONENT"
                ? "bg-gradient-to-r from-cyan-50 to-white"
                : "bg-gradient-to-r from-teal-50 to-white",
              {
                "pl-0": showingDiagramOnHeader,
              }
            )}
          >
            {getProductIcon(
              undefined,
              showingDiagramOnHeader ? diagramURL : undefined
            )}
            {searchStore.currentDetailNode?.name}
          </div>
        )
      )}

      {searchStore.currentDetailNode?.type !== "COMPONENT" && !imageURL && (
        <SectionsNavMenu
          clickedMenuItem={activeSection}
          menuItems={sectionItems}
          moveToSection={moveToSection}
          containerClassName={
            searchStore.currentDetailNode?.type != "COMPONENT" && isComponent
              ? "top-0"
              : ""
          }
        />
      )}

      <div className="p-6">
        {showAlerts()}

        <div>
          {sectionItems.map((item, index) => (
            <section
              id={item.id}
              key={index}
              ref={item.ref}
              className="scroll-smooth"
            >
              {item.content}
            </section>
          ))}
        </div>

        {isComponent ? (
          <ComponentDetails />
        ) : (
          <ProductDetails epdGroupsData={details?.epd_groups} />
        )}
      </div>
    </div>
  );
}

export default observer(DetailsView);
